import React, {
  useRef,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Container, Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import ContentWindow from "../../shared/Displays/ContentWindow";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { ASSET_NAME } from "../../../../assets";
import ButtonYes from "../Buttons/ButtonYes";
import ButtonNo from "../Buttons/ButtonNo";
import ButtonClose from "../Buttons/ButtonClose";
import anime from "animejs/lib/anime.es.js";

const PixiPopupWindow = forwardRef(
  (
    {
      children,
      name = "pixi_popup_window",
      fadeDuration = 200,
      showButtons = false,
      onYesClick,
      onNoClick,
      showCloseButton = false,
      onCloseClick,
      baseFontSize = 32, // Allow override of base font size
      title, // Optional title prop
    },
    ref
  ) => {
    const { device, orientation, dimensions, uiSymbolScale } = useDimensions();
    const [windowAlpha, setWindowAlpha] = useState(0);
    const windowRef = useRef(null);
    const animationRef = useRef(null);

    // Expose fade out method via ref
    useImperativeHandle(ref, () => ({
      fadeOut: (callback) => {
        if (animationRef.current) {
          animationRef.current.pause();
        }

        animationRef.current = anime({
          targets: { alpha: windowAlpha },
          alpha: 0,
          duration: fadeDuration,
          easing: "easeInQuad",
          update: (anim) => {
            setWindowAlpha(anim.animations[0].currentValue);
          },
          complete: () => {
            if (callback && typeof callback === "function") callback();
          },
        });
      },
    }));

    // Responsive window dimensions with symbol scaling
    const getResponsiveWindowSize = () => {
      let deviceMultiplier = 1.0; // Default full size

      if (device === PixiSettings.DEVICE.MOBILE) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          // Mobile portrait: bigger size
          deviceMultiplier = 1.5;
        } else {
          // Mobile landscape: standard size
          deviceMultiplier = 1.4;
        }
      } else if (device === PixiSettings.DEVICE.TABLET) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          // Tablet portrait: bigger size
          deviceMultiplier = 1.42;
        } else {
          // Tablet landscape: standard size
          deviceMultiplier = 1.2;
        }
      } else {
        // Desktop: standard size
        deviceMultiplier = 1.0;
      }

      // Use symbol scaling factor from DimensionsContext

      // Use PixiSettings max dimensions as base
      const maxWindowWidth = PixiSettings.POPUP_WINDOW_MAX_WIDTH;
      const maxWindowHeight = PixiSettings.POPUP_WINDOW_MAX_HEIGHT;

      // Apply both device multiplier and symbol scaling
      const finalWidth = maxWindowWidth * deviceMultiplier * uiSymbolScale;
      const finalHeight = maxWindowHeight * deviceMultiplier * uiSymbolScale;

      return {
        width: Math.min(finalWidth, maxWindowWidth), // Don't exceed max even with scaling
        height: Math.min(finalHeight, maxWindowHeight),
      };
    };

    const windowSize = getResponsiveWindowSize();

    // Calculate responsive button size (same pattern as PixiButtonHome)
    const getResponsiveButtonSize = () => {
      let multiplier = 1.0; // Default full size

      if (device === PixiSettings.DEVICE.MOBILE) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          // Mobile portrait: bigger size
          multiplier = 1.15;
        } else {
          // Mobile landscape: standard size
          multiplier = 1.1;
        }
      } else if (device === PixiSettings.DEVICE.TABLET) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          // Tablet portrait: slightly bigger size
          multiplier = 1.05;
        } else {
          // Tablet landscape: slightly smaller size
          multiplier = 0.9;
        }
      } else {
        // Desktop: standard size
        multiplier = 0.75;
      }

      const buttonBaseWidth = PixiSettings.POPUP_BUTTON_MAX_WIDTH;
      const buttonBaseHeight = PixiSettings.POPUP_BUTTON_MAX_HEIGHT;
      const buttonWidth = Math.min(
        buttonBaseWidth * multiplier * uiSymbolScale,
        buttonBaseWidth
      );
      const buttonHeight = Math.min(
        buttonBaseHeight * multiplier * uiSymbolScale,
        buttonBaseHeight
      );

      return { buttonWidth, buttonHeight };
    };

    // Calculate button positions relative to window center (adapts to actual button size)
    const getButtonPositions = () => {
      if (!showButtons) return { yesX: 0, noX: 0, buttonY: 0 };

      const { buttonWidth, buttonHeight } = getResponsiveButtonSize();

      // Check if we have both buttons or just Yes button
      const hasBothButtons = onYesClick && onNoClick;

      let yesX, noX;

      if (hasBothButtons) {
        // Two buttons: position with spacing like before
        const spacing = buttonWidth / 1.25;
        yesX = -spacing;
        noX = spacing;
      } else {
        // Single button: center it
        yesX = 0;
        noX = 0; // Won't be used but keep for consistency
      }

      // Position buttons vertically based on ACTUAL button height from bottom of window
      const buttonY = windowSize.height / 2 - buttonHeight * 1.5;

      return { yesX, noX, buttonY, buttonWidth, buttonHeight };
    };

    const { yesX, noX, buttonY, buttonWidth, buttonHeight } =
      getButtonPositions();

    // Fade in window when component mounts
    useEffect(() => {
      if (windowRef.current) {
        anime({
          targets: { alpha: 0 },
          alpha: 1,
          duration: fadeDuration,
          easing: "easeOutQuad",
          update: (anim) => {
            setWindowAlpha(anim.animations[0].currentValue);
          },
        });
      }
    }, [fadeDuration]);

    // Use popup frame PNG texture
    const popupFrameTexture = PIXI.Assets.get(
      ASSET_NAME.popup_frame_horizontal
    );

    if (!popupFrameTexture) {
      return null; // Don't render if texture not loaded
    }

    // Handle clicks on the window container to prevent them from bubbling to overlay
    const handleWindowClick = (event) => {
      // Stop click propagation to prevent closing modal when clicking window content
      event.stopPropagation();
    };

    return (
      <Container
        ref={windowRef}
        name={name}
        x={dimensions.width / 2} // Center horizontally
        y={dimensions.height / 2} // Center vertically
        zIndex={10} // Above overlay (zIndex 9)
        alpha={windowAlpha}
        interactive={true}
        onclick={handleWindowClick}
      >
        <Sprite
          texture={popupFrameTexture}
          anchor={0.5}
          width={windowSize.width}
          height={windowSize.height}
        />

        {/* Render content using ContentWindow for automatic font scaling */}
        {children && (
          <ContentWindow
            name={`${name}_content`}
            windowSize={windowSize}
            baseFontSize={baseFontSize}
            title={title}
          >
            {children}
          </ContentWindow>
        )}

        {showButtons && (
          <>
            {onYesClick && (
              <ButtonYes
                x={yesX}
                y={buttonY}
                width={buttonWidth}
                height={buttonHeight}
                onClick={onYesClick}
              />
            )}
            {onNoClick && (
              <ButtonNo
                x={noX}
                y={buttonY}
                width={buttonWidth}
                height={buttonHeight}
                onClick={onNoClick}
              />
            )}
          </>
        )}
        {showCloseButton && (
          <ButtonClose windowSize={windowSize} onClick={onCloseClick} />
        )}
      </Container>
    );
  }
);

PixiPopupWindow.displayName = "PixiPopupWindow";

export default PixiPopupWindow;
